import * as React from 'react';

import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import {
  Button,
  cn,
  Menu,
  Popover,
  PopoverDialog,
  PopoverTrigger,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import {
  LucideMoreVertical,
  LucidePlus,
  LucidePrinter,
  LucideRoute,
  LucideSend,
  LucideTruck,
  LucideContainer,
} from 'lucide-react';

import {
  $getLocalServices,
  $pendingCountByDriver,
} from '../../state/loadBoard.localState';
import type { DriverRecord } from '../../types';
import { TrailerSelect } from '../@shared/TrailerSelect';
import { TruckSelect } from '../@shared/TruckSelect';

interface DriverCardHeaderProps {
  driver: DriverRecord;
  excludeTruckIds: string[];
  excludeTrailerIds: string[];
  onUpdateDefaultTruck: (truckId: string | null) => void;
  onUpdateDefaultTrailer: (trailerId: string | null) => void;
  onCreateTask: () => void;
  onSubmitToQueue: () => void;
}

export const DriverCardHeader = ({
  driver,
  excludeTruckIds,
  excludeTrailerIds,
  onUpdateDefaultTruck,
  onUpdateDefaultTrailer,
  onCreateTask,
  onSubmitToQueue,
}: DriverCardHeaderProps) => {
  const t = useTranslations();
  const pendingCountByDriver = useAtomValue($pendingCountByDriver);
  const pendingCount = pendingCountByDriver.get(driver.id) ?? 0;
  const localServices = useAtomValue($getLocalServices);

  const statusCounts = React.useMemo(() => {
    const counts = { active: 0, done: 0, planned: 0, draft: 0 };
    for (const svc of localServices) {
      if (svc.driverId !== driver.id) continue;
      if (svc.status === 'Active' || svc.status === 'InProgress')
        counts.active++;
      else if (svc.status === 'Completed') counts.done++;
      else if (svc.status === 'Planned') counts.planned++;
      else if (svc.status === 'Draft') counts.draft++;
    }
    return counts;
  }, [localServices, driver.id]);

  const truckLabel =
    driver.currentTruck?.licensePlate ?? t.loadBoardView.driverCard.noTruck;
  const trailerLabel =
    driver.currentTrailer?.licensePlate ?? t.loadBoardView.driverCard.noTrailer;

  const handleTruckSelect = React.useCallback(
    (value: React.Key | null) => {
      onUpdateDefaultTruck(value ? String(value) : null);
    },
    [onUpdateDefaultTruck],
  );

  const handleTrailerSelect = React.useCallback(
    (value: string | number | null) => {
      onUpdateDefaultTrailer(value ? String(value) : null);
    },
    [onUpdateDefaultTrailer],
  );

  const countsText = [
    statusCounts.active > 0 &&
      `${statusCounts.active} ${t.loadBoardView.driverCard.active}`,
    statusCounts.done > 0 &&
      `${statusCounts.done} ${t.loadBoardView.driverCard.done}`,
    statusCounts.planned > 0 &&
      `${statusCounts.planned} ${t.loadBoardView.driverCard.planned}`,
    statusCounts.draft > 0 &&
      `${statusCounts.draft} ${t.loadBoardView.driverCard.draft}`,
  ]
    .filter(Boolean)
    .join(' \u00b7 ');

  return (
    <div className="@DriverCardHeader flex w-full flex-1 flex-col">
      <div>
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1 truncate text-sm leading-6 font-medium">
            {driver.firstName} {driver.lastName}
          </div>
          <div className="shrink-0" onPointerDown={(e) => e.stopPropagation()}>
            <Menu
              trigger={
                <Button
                  size="icon"
                  variant="outline"
                  className="!size-6"
                  icon={<LucideMoreVertical className="size-3.5" />}
                />
              }
              placement="bottom end"
              items={[
                {
                  label: t.loadBoardView.driverCard.createTask,
                  icon: <LucidePlus className="size-4" />,
                  onAction: onCreateTask,
                },
                {
                  label:
                    pendingCount > 0
                      ? `Submit ${pendingCount} task${pendingCount > 1 ? 's' : ''} to queue`
                      : t.loadBoardView.driverCard.submitToQueue,
                  icon: <LucideSend className="size-4" />,
                  isDisabled: pendingCount === 0,
                  onAction: onSubmitToQueue,
                },
                {
                  label: t.loadBoardView.driverCard.createRoute,
                  icon: <LucideRoute className="size-4" />,
                  isDisabled: true,
                },
                {
                  label: t.loadBoardView.driverCard.printTasks,
                  icon: <LucidePrinter className="size-4" />,
                  isDisabled: true,
                },
              ]}
            />
          </div>
        </div>
        <div
          className="mt-0.5 flex items-center gap-1.5 overflow-hidden text-[10px] text-foreground/60"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <PopoverTrigger>
            <Button
              variant="ghost"
              size="small"
              className={cn(
                '!h-auto !min-h-0 min-w-0 flex-1 !justify-start !gap-0.5 overflow-hidden !rounded !px-0.5 !py-0 !text-[10px] !font-normal text-foreground/60 hover:bg-foreground/5',
                !driver.currentTruck && 'italic opacity-50',
              )}
            >
              <LucideTruck className="size-2.5 shrink-0" />
              <span className="truncate">{truckLabel}</span>
            </Button>
            <Popover placement="bottom start">
              <PopoverDialog>
                <div className="w-64 p-2">
                  <TruckSelect
                    value={driver.defaultTruckId}
                    onChange={handleTruckSelect}
                    label={t.loadBoardView.driverCard.defaultTruck}
                    autoFocus
                    excludeIds={excludeTruckIds.filter(
                      (id) => id !== driver.defaultTruckId,
                    )}
                  />
                </div>
              </PopoverDialog>
            </Popover>
          </PopoverTrigger>
          <PopoverTrigger>
            <Button
              variant="ghost"
              size="small"
              className={cn(
                '!h-auto !min-h-0 min-w-0 flex-1 !justify-start !gap-0.5 overflow-hidden !rounded !px-0.5 !py-0 !text-[10px] !font-normal text-foreground/60 hover:bg-foreground/5',
                !driver.currentTrailer && 'italic opacity-50',
              )}
            >
              <LucideContainer className="size-2.5 shrink-0" />
              <span className="truncate">{trailerLabel}</span>
            </Button>
            <Popover placement="bottom start">
              <PopoverDialog>
                <div className="w-64 p-2">
                  <TrailerSelect
                    value={driver.defaultTrailerId}
                    onChange={handleTrailerSelect}
                    label={t.loadBoardView.driverCard.defaultTrailer}
                    autoFocus
                    excludeIds={excludeTrailerIds.filter(
                      (id) => id !== driver.defaultTrailerId,
                    )}
                  />
                </div>
              </PopoverDialog>
            </Popover>
          </PopoverTrigger>
        </div>
      </div>

      {countsText && (
        <div className="mt-auto text-[10px] text-foreground/40">
          {countsText}
        </div>
      )}
    </div>
  );
};
